import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  AvatarGroup,
  Grid,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  WorkOutline,
  CheckCircleOutline,
  Schedule,
  GroupsOutlined,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { timesheetService } from "@/services/timesheet.service";
import { CircularProgress } from "@mui/material";
import { useAuth } from "@/contexts/AuthContext";
// import type { Project, TimeEntry } from "@/types";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

interface ProjectMember {
  employeeId: number;
  employeeName: string;
  role: string;
  totalHours: number;
}

interface MyProjectDetail {
  id: number;
  projectNumber: string;
  projectName: string;
  clientName: string;
  status: string;
  startDate: string;
  endDate: string;
  budgetHours: number;
  workedHours: number;
  myHours: number;
  progress: number;
  teamMembers: ProjectMember[];
  myRole: string;
}

export default function MyProjectTeamsPage() {
  const { user } = useAuth();
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    new Set()
  );
  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  const {
    data: _projectsResponse,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: () => timesheetService.getProjects(),
    retry: 1,
  });

  const {
    data: _timeEntriesResponse,
    isLoading: timeEntriesLoading,
    error: timeEntriesError,
  } = useQuery({
    queryKey: ["myTeamTimeEntries", startDate, endDate],
    queryFn: () => {
      return timesheetService.getTimeEntries({
        startDate: startDate,
        endDate: endDate,
      });
    },
    retry: 1,
  });

  // Get project members for the current user
  const { data: _myProjectMembersResponse, isLoading: membersLoading } =
    useQuery({
      queryKey: ["myProjectMembers", user?.id, _timeEntriesResponse],
      queryFn: async () => {
        if (!user?.id || !_timeEntriesResponse?.data)
          return Promise.resolve({ data: [] });

        // Find my employee ID from my own time entries
        const myTimeEntry = _timeEntriesResponse.data.find(
          (entry: any) =>
            entry.userEmail?.toLowerCase() === user.email?.toLowerCase() ||
            entry.userName
              ?.toLowerCase()
              .includes(user.firstName?.toLowerCase()) ||
            entry.userName?.toLowerCase().includes(user.lastName?.toLowerCase())
        );

        if (!myTimeEntry?.userId) {
          console.log("Could not find employee ID from time entries");
          return Promise.resolve({ data: [] });
        }

        console.log("Found my employee ID:", myTimeEntry.userId);
        return timesheetService.getEmployeeProjects(myTimeEntry.userId, true);
      },
      enabled: !!user?.id && !!_timeEntriesResponse?.data,
      retry: 1,
    });

  const isLoading = projectsLoading || timeEntriesLoading || membersLoading;
  const error = projectsError || timeEntriesError;

  const projectsResponse = _projectsResponse;
  const timeEntriesResponse = _timeEntriesResponse;
  const myProjectMembersResponse = _myProjectMembersResponse;

  const myProjects: MyProjectDetail[] = (() => {
    // Use real data if available
    if (!projectsResponse?.data || !timeEntriesResponse?.data || !user) {
      console.log("Missing data:", {
        hasProjects: !!projectsResponse?.data,
        hasTimeEntries: !!timeEntriesResponse?.data,
        hasUser: !!user,
      });
      return [];
    }

    const projects = projectsResponse.data;
    const timeEntries = timeEntriesResponse.data;
    const myProjectMembers = myProjectMembersResponse?.data || [];

    console.log(
      "All time entries:",
      timeEntries.map((e: any) => ({
        userId: e.userId,
        userName: e.userName,
        userEmail: e.userEmail,
        projectId: e.projectId,
      }))
    );

    console.log("Looking for user:", {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    });

    // Find my employee ID from my time entries - try multiple strategies
    let myTimeEntry = timeEntries.find(
      (entry: any) =>
        entry.userEmail?.toLowerCase() === user.email?.toLowerCase()
    );

    if (!myTimeEntry) {
      myTimeEntry = timeEntries.find(
        (entry: any) =>
          entry.userName
            ?.toLowerCase()
            .includes(user.firstName?.toLowerCase()) &&
          entry.userName?.toLowerCase().includes(user.lastName?.toLowerCase())
      );
    }

    if (!myTimeEntry) {
      myTimeEntry = timeEntries.find(
        (entry: any) =>
          entry.userName
            ?.toLowerCase()
            .includes(user.firstName?.toLowerCase()) ||
          entry.userName?.toLowerCase().includes(user.lastName?.toLowerCase())
      );
    }

    const currentUserId = myTimeEntry?.userId;

    console.log(
      "Found employee ID:",
      currentUserId,
      "from entry:",
      myTimeEntry
    );

    if (!currentUserId) {
      console.log(
        "Could not find employee ID - showing all projects with time entries"
      );
      // Fallback: show all projects that have any time entries
      const projectsWithEntries = new Set(
        timeEntries.map((e: any) => e.projectId)
      );

      return projects
        .filter((project: any) => projectsWithEntries.has(project.id))
        .map((project: any) => {
          const projectEntries = timeEntries.filter(
            (entry: any) => entry.projectId === project.id
          );

          const memberMap = new Map<number, ProjectMember>();
          projectEntries.forEach((entry: any) => {
            const employeeId = entry.userId || 0;
            if (!memberMap.has(employeeId)) {
              memberMap.set(employeeId, {
                employeeId,
                employeeName: entry.userName || `Medewerker ${employeeId}`,
                role: "Teamlid",
                totalHours: 0,
              });
            }
            const member = memberMap.get(employeeId)!;
            member.totalHours += entry.hours;
          });

          const teamMembers = Array.from(memberMap.values());
          const totalWorkedHours = teamMembers.reduce(
            (sum, m) => sum + m.totalHours,
            0
          );
          const budgetHours = project.budgetHours || 0;
          const progress =
            budgetHours > 0 ? (totalWorkedHours / budgetHours) * 100 : 0;

          return {
            id: project.id,
            projectNumber: project.code || `PRJ-${project.id}`,
            projectName: project.name,
            clientName: project.clientName || "Onbekend",
            status: project.isActive ? "Actief" : "Afgerond",
            startDate: project.startDate || new Date().toISOString(),
            endDate: project.endDate || new Date().toISOString(),
            budgetHours,
            workedHours: totalWorkedHours,
            myHours: 0,
            progress: Math.min(progress, 100),
            teamMembers,
            myRole: "Bekijk team",
          };
        })
        .sort((a, b) => b.workedHours - a.workedHours);
    }

    console.log("Data loaded:", {
      projectsCount: projects.length,
      timeEntriesCount: timeEntries.length,
      myProjectMembersCount: myProjectMembers.length,
      currentUserId,
      userEmail: user.email,
    });

    // Get project IDs where I have time entries
    const myTimeEntryProjectIds = new Set(
      timeEntries
        .filter((entry: any) => entry.userId === currentUserId)
        .map((entry: any) => entry.projectId)
    );

    // Get project IDs where I'm a member
    const myProjectMemberIds = new Set(
      myProjectMembers.map((pm: any) => pm.projectId)
    );

    // Combine both sets - show projects where I have time entries OR where I'm a member
    const myProjectIds = new Set([
      ...myTimeEntryProjectIds,
      ...myProjectMemberIds,
    ]);

    console.log("My project IDs:", Array.from(myProjectIds));

    // Filter projects where I'm involved
    return projects
      .filter((project: any) => myProjectIds.has(project.id))
      .map((project: any) => {
        // Get time entries for this project
        const projectEntries = timeEntries.filter(
          (entry: any) => entry.projectId === project.id
        );

        // Group by employee
        const memberMap = new Map<number, ProjectMember>();
        let myTotalHours = 0;

        projectEntries.forEach((entry: any) => {
          const employeeId = entry.userId || 0;
          if (!memberMap.has(employeeId)) {
            memberMap.set(employeeId, {
              employeeId,
              employeeName: entry.userName || `Medewerker ${employeeId}`,
              role: "Teamlid",
              totalHours: 0,
            });
          }
          const member = memberMap.get(employeeId)!;
          member.totalHours += entry.hours;

          if (employeeId === currentUserId) {
            myTotalHours += entry.hours;
          }
        });

        const teamMembers = Array.from(memberMap.values());
        const totalWorkedHours = teamMembers.reduce(
          (sum, m) => sum + m.totalHours,
          0
        );
        const budgetHours = project.budgetHours || 0;
        const progress =
          budgetHours > 0 ? (totalWorkedHours / budgetHours) * 100 : 0;

        // Get my role from project members
        const myMembership = myProjectMembers.find(
          (pm: any) => pm.projectId === project.id
        );
        const myRole =
          myMembership?.projectRoleName ||
          (myMembership?.isProjectManager ? "Project Manager" : "Teamlid");

        return {
          id: project.id,
          projectNumber: project.code || `PRJ-${project.id}`,
          projectName: project.name,
          clientName: project.clientName || "Onbekend",
          status: project.isActive ? "Actief" : "Afgerond",
          startDate: project.startDate || new Date().toISOString(),
          endDate: project.endDate || new Date().toISOString(),
          budgetHours,
          workedHours: totalWorkedHours,
          myHours: myTotalHours,
          progress: Math.min(progress, 100),
          teamMembers,
          myRole,
        };
      })
      .sort((a, b) => b.myHours - a.myHours); // Sort by my hours descending
  })();

  const currentUserId =
    myProjects.length > 0
      ? myProjects[0].teamMembers.find((m: ProjectMember) =>
          timeEntriesResponse?.data?.some(
            (e: any) =>
              e.userId === m.employeeId &&
              (e.userEmail?.toLowerCase() === user?.email?.toLowerCase() ||
                e.userName
                  ?.toLowerCase()
                  .includes(user?.firstName?.toLowerCase()))
          )
        )?.employeeId || 0
      : 0;
  const activeProjects = myProjects.filter((p) => p.status === "Actief");
  const completedProjects = myProjects.filter((p) => p.status === "Afgerond");

  const displayProjects = activeTab === 0 ? activeProjects : completedProjects;

  const toggleProjectExpansion = (projectId: number) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const getProgressColor = (progress: number) => {
    if (progress < 50) return "success";
    if (progress < 80) return "warning";
    return "error";
  };

  if (isLoading) {
    return (
      <Layout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box p={3}>
          <Typography variant="h6" color="error">
            Fout bij laden van projecten
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Mijn Project Teams
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 3 }}
        >
          Overzicht van projecten waar jij bij betrokken bent
        </Typography>

        {/* Date Range Filter */}
        <Box mb={3} display="flex" gap={2} alignItems="center">
          <TextField
            label="Van"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Tot"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>

        {myProjects.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <Box textAlign="center">
              <Typography variant="h6" gutterBottom>
                Geen projecten gevonden
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Je bent nog niet toegewezen aan projecten of hebt nog geen uren
                geregistreerd.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <WorkOutline color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        Actieve Projecten
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {activeProjects.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircleOutline color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        Afgeronde Projecten
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {completedProjects.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Schedule color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        Totaal Mijn Uren
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {myProjects.reduce((sum, p) => sum + p.myHours, 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <GroupsOutlined color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        Teamleden Totaal
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {
                        new Set(
                          myProjects.flatMap((p) =>
                            p.teamMembers.map((m) => m.employeeId)
                          )
                        ).size
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Box mb={3}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label={`Actief (${activeProjects.length})`} />
                <Tab label={`Afgerond (${completedProjects.length})`} />
              </Tabs>
            </Box>

            {/* Project Cards */}
            {displayProjects.map((project) => (
              <Card key={project.id} sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    sx={{ flexWrap: 'wrap', gap: 2 }}
                  >
                    <Box display="flex" alignItems="center" gap={2} sx={{ flex: '1 1 240px', minWidth: 0 }}>
                      <Tooltip title={expandedProjects.has(project.id) ? "Inklappen" : "Uitklappen"}><IconButton
                        aria-label={`${project.projectName} ${expandedProjects.has(project.id) ? 'inklappen' : 'uitklappen'}`}
                        aria-expanded={expandedProjects.has(project.id)}
                        onClick={() => toggleProjectExpansion(project.id)}
                        size="small"
                      >
                        {expandedProjects.has(project.id) ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton></Tooltip>
                      <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        <Typography variant="h6">
                          {project.projectNumber} - {project.projectName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {project.clientName}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap', maxWidth: '100%', '& .MuiChip-root': { maxWidth: '100%' } }}>
                      <Chip
                        label={project.status}
                        color={
                          project.status === "Actief" ? "success" : "default"
                        }
                        size="small"
                      />
                      <Chip
                        label={project.myRole}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {/* Collapsed View - Summary */}
                  {!expandedProjects.has(project.id) && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Mijn Uren
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          {project.myHours}u
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Totaal Gewerkt
                        </Typography>
                        <Typography variant="h6">
                          {project.workedHours}u
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Budget
                        </Typography>
                        <Typography variant="h6">
                          {project.budgetHours}u
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Voortgang
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6">
                            {project.progress.toFixed(0)}%
                          </Typography>
                          <Chip
                            label={
                              project.progress >= 100 ? "Volledig" : "Lopend"
                            }
                            color={getProgressColor(project.progress)}
                            size="small"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={2} sx={{ flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary">
                            Team:
                          </Typography>
                          <AvatarGroup max={5}
                            slotProps={{ additionalAvatar: { sx: { '&&': { width: 'auto', minWidth: 32, px: 0.5 } } } }}
                            sx={{ flexShrink: 0, "& .MuiAvatar-root": { width: 32, height: 32, fontSize: "1rem", lineHeight: 1 } }}>
                            {project.teamMembers.map((member) => (
                              <Avatar
                                key={member.employeeId}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: "primary.main",
                                }}
                              >
                                {member.employeeName.trim().charAt(0).toLocaleUpperCase("nl-NL")}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                          <Typography variant="body2" color="text.secondary">
                            ({project.teamMembers.length} leden)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {/* Expanded View - Details */}
                  <Collapse
                    in={expandedProjects.has(project.id)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box mt={3}>
                      <Grid container spacing={3} mb={3}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Periode
                          </Typography>
                          <Typography variant="body1">
                            {format(
                              new Date(project.startDate),
                              "dd MMM yyyy",
                              { locale: nl }
                            )}{" "}
                            -{" "}
                            {format(new Date(project.endDate), "dd MMM yyyy", {
                              locale: nl,
                            })}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Budget Status
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                width: "100%",
                                height: 8,
                                bgcolor: "grey.200",
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${Math.min(project.progress, 100)}%`,
                                  height: "100%",
                                  bgcolor:
                                    project.progress >= 100
                                      ? "error.main"
                                      : project.progress >= 80
                                      ? "warning.main"
                                      : "success.main",
                                }}
                              />
                            </Box>
                            <Typography variant="body2" fontWeight="bold">
                              {project.progress.toFixed(0)}%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Typography variant="h6" gutterBottom>
                        Team Leden ({project.teamMembers.length})
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Naam</TableCell>
                              <TableCell>Rol</TableCell>
                              <TableCell align="right">Totaal Uren</TableCell>
                              <TableCell align="right">% van Budget</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {project.teamMembers
                              .sort((a, b) => b.totalHours - a.totalHours)
                              .map((member) => {
                                const isMe =
                                  member.employeeId === currentUserId;
                                return (
                                  <TableRow
                                    key={member.employeeId}
                                    sx={{
                                      bgcolor: isMe ? "primary.50" : "inherit",
                                    }}
                                  >
                                    <TableCell>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <Avatar
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: isMe
                                              ? "primary.main"
                                              : "grey.400",
                                            fontSize: "0.75rem",
                                            lineHeight: 1,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {member.employeeName.trim().charAt(0).toLocaleUpperCase("nl-NL")}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                          {member.employeeName}
                                          {isMe && (
                                            <Chip
                                              label="Jij"
                                              size="small"
                                              color="primary"
                                              sx={{ ml: 1, height: 20 }}
                                            />
                                          )}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>{member.role}</TableCell>
                                    <TableCell align="right">
                                      <strong>{member.totalHours}u</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                      {project.budgetHours > 0
                                        ? (
                                            (member.totalHours /
                                              project.budgetHours) *
                                            100
                                          ).toFixed(1)
                                        : 0}
                                      %
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                              <TableCell colSpan={2}>
                                <strong>Totaal</strong>
                              </TableCell>
                              <TableCell align="right">
                                <strong>{project.workedHours}u</strong>
                              </TableCell>
                              <TableCell align="right">
                                <strong>{project.progress.toFixed(1)}%</strong>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    </Layout>
  );
}
